// Editor popovers — keyboard behaviour for the shared shell in
// landing_page_editor_popovers.templ. Target: the `#editor-popover`
// backdrop that HTMX drops into `#editor-popover-host`.
//
// The shell is a modal: it covers the page and takes the click. Without
// this it could only be dismissed by clicking the backdrop, and Tab
// walked straight out of it into the editor behind.

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

// Everything a keyboard can land on inside the panel. Deliberately
// narrow — the popovers are forms and button lists, not arbitrary HTML.
const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

// The element to hand focus back to. Held at module scope rather than
// per popover: a form inside the shell swaps the whole popover for a
// re-rendered one, and focus should still return to the toolbar button
// that opened the first.
let returnFocusTo = null;

export function initEditorPopover(root = document) {
  const popover =
    root.id === "editor-popover"
      ? root
      : root.querySelector?.("#editor-popover");
  if (!popover || !firstBind(popover)) return;

  // Only the first render of a popover records where focus came from;
  // by the time a swap re-renders it, focus is already inside. The
  // isConnected check matters because a swap that replaces the whole
  // page leaves a detached node here, which would otherwise block
  // every later popover from recording its own opener.
  if (
    (!returnFocusTo || !returnFocusTo.isConnected) &&
    !popover.contains(document.activeElement)
  ) {
    returnFocusTo = document.activeElement;
  }

  nameDialog(popover);
  lockBackground();
  focusFirst(popover);
  popover.addEventListener("keydown", (event) => trapTab(popover, event));
}

// closeEditorPopover removes the open popover and returns focus where
// it came from. Exposed on window because the shell and its Cancel
// button close from inline handlers.
export function closeEditorPopover() {
  document.getElementById("editor-popover")?.remove();
  unlockBackground();

  const target = returnFocusTo;
  returnFocusTo = null;
  target?.focus?.();
}

// nameDialog points aria-labelledby at the panel's own heading, so the
// dialog announces as what it is rather than "dialog".
function nameDialog(popover) {
  const heading = popover.querySelector("h2, h3");
  if (!heading) return;

  if (!heading.id) heading.id = "editor-popover-title";
  popover.setAttribute("aria-labelledby", heading.id);
}

// The backdrop covers the page but does not stop it scrolling behind.
// A swap that replaces <body> drops the inline style with it, so a
// leaked lock heals itself.
function lockBackground() {
  document.body.style.overflow = "hidden";
}

function unlockBackground() {
  document.body.style.overflow = "";
}

function focusFirst(popover) {
  const first = popover.querySelector(FOCUSABLE);
  // preventScroll: the popover is centred in the viewport already, and
  // focusing a control inside it must not jerk the page behind.
  first?.focus?.({ preventScroll: true });
}

// trapTab keeps Tab inside the panel by wrapping at either end.
function trapTab(popover, event) {
  if (event.key !== "Tab") return;

  const items = [...popover.querySelectorAll(FOCUSABLE)].filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
  if (items.length === 0) return;

  const first = items[0];
  const last = items[items.length - 1];

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

// Escape is bound once on the document rather than per popover, so it
// works no matter which element inside currently holds focus.
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && document.getElementById("editor-popover")) {
    closeEditorPopover();
  }
});

window.closeEditorPopover = closeEditorPopover;
